import {
  Controller,
  Post,
  Get,
  Param,
  Body,
  Res,
  HttpStatus,
  Logger,
} from '@nestjs/common';
import { ApiExcludeController } from '@nestjs/swagger';
import type { Response } from 'express';
import {
  CapabilityService,
  CapabilityNotFoundError,
  ActionNotFoundError,
} from '../services/capability.service';
import { PluginLoaderService, PluginNotFoundError } from '../services/plugin-loader.service';
import { TemplateEngineService } from '../services/template-engine.service';
import type {
  CapabilityConfig,
  SuccessResponse,
  ErrorResponse,
  DebugExecuteResponseData,
  ListResponseData,
  StreamContentResponse,
  StreamErrorResponse,
} from '../interfaces';
import { ErrorCodes } from '../interfaces';
import { stringifyForLog } from '../utils';

interface DebugRequestBody {
  action?: string;
  params?: Record<string, unknown>;
  capability?: CapabilityConfig;
}

@ApiExcludeController()
@Controller('__innerapi__/capability')
export class DebugController {
  private readonly logger = new Logger(DebugController.name);

  constructor(
    private readonly capabilityService: CapabilityService,
    private readonly pluginLoaderService: PluginLoaderService,
    private readonly templateEngineService: TemplateEngineService,
  ) {}

  @Get('list')
  list(@Res() res: Response): void {
    try {
      const capabilities = this.capabilityService.listCapabilities();

      res.status(HttpStatus.OK).json({
        status_code: ErrorCodes.SUCCESS,
        data: {
          capabilities: capabilities.map(c => ({
            id: c.id,
            name: c.name,
            pluginID: c.pluginKey,
            pluginVersion: c.pluginVersion,
          })),
        },
      } satisfies SuccessResponse<ListResponseData>);
    } catch (error) {
      this.logger.error('Failed to list capabilities', error);
      res.status(HttpStatus.INTERNAL_SERVER_ERROR).json({
        status_code: ErrorCodes.EXECUTION_ERROR,
        error_msg: `Failed to list capabilities: ${error instanceof Error ? error.message : String(error)}`,
      } satisfies ErrorResponse);
    }
  }

  /**
   * 获取 capability 配置
   * 优先使用 body.capability，否则从服务获取
   */
  private getCapabilityConfig(
    capabilityId: string,
    bodyCapability?: CapabilityConfig,
  ): CapabilityConfig {
    if (bodyCapability) {
      return bodyCapability;
    }

    const config = this.capabilityService.getCapability(capabilityId);
    if (!config) {
      throw new CapabilityNotFoundError(capabilityId);
    }

    return config;
  }

  /**
   * 获取 action 名称
   * 优先使用传入的 action，否则使用插件第一个 action
   */
  private async getActionName(pluginKey: string, formValue?: unknown, bodyAction?: string): Promise<string> {
    if (bodyAction) {
      return bodyAction;
    }

    // 根据 manifest 决定是否传入 config
    const isConfigRequired = this.pluginLoaderService.isConfigRequired(pluginKey);
    const pluginInstance = isConfigRequired
      ? await this.pluginLoaderService.loadPlugin(pluginKey, formValue)
      : await this.pluginLoaderService.loadPlugin(pluginKey);

    const actions = pluginInstance.listActions();

    if (actions.length === 0) {
      throw new Error(`Plugin ${pluginKey} has no actions`);
    }

    return actions[0];
  }

  @Post('debug/:capability_id')
  async debug(
    @Param('capability_id') capabilityId: string,
    @Body() body: DebugRequestBody,
    @Res() res: Response,
  ): Promise<void> {
    const startTime = Date.now();
    const params = body.params ?? {};

    try {
      const config = this.getCapabilityConfig(capabilityId, body.capability);
      const action = await this.getActionName(config.pluginKey, config.formValue, body.action);

      const resolvedParams = this.templateEngineService.resolve(
        config.formValue,
        params,
        config.paramsSchema,
      );

      const result = await this.capabilityService
        .loadWithConfig(config)
        .call(action, params, { isDebug: true, caller: 'debug' });

      res.status(HttpStatus.OK).json({
        status_code: ErrorCodes.SUCCESS,
        data: {
          output: result,
          debug: {
            capabilityConfig: config,
            resolvedParams,
            duration: Date.now() - startTime,
            pluginID: config.pluginKey,
            action,
          },
        },
      } satisfies SuccessResponse<DebugExecuteResponseData>);
    } catch (error) {
      this.logger.error('Debug execution failed', error);
      res.status(HttpStatus.INTERNAL_SERVER_ERROR).json(
        this.buildErrorResponse(error),
      );
    }
  }

  /**
   * 构建错误响应
   */
  private buildErrorResponse(error: unknown): ErrorResponse {
    if (error instanceof CapabilityNotFoundError) {
      return {
        status_code: ErrorCodes.CAPABILITY_NOT_FOUND,
        error_msg: `Capability not found: ${error.capabilityId}`,
      };
    }
    if (error instanceof PluginNotFoundError) {
      return {
        status_code: ErrorCodes.PLUGIN_NOT_FOUND,
        error_msg: `Plugin not found, please install plugin: ${error.pluginKey}`,
      };
    }
    if (error instanceof ActionNotFoundError) {
      return {
        status_code: ErrorCodes.ACTION_NOT_FOUND,
        error_msg: `Action '${error.actionName}' not found in plugin ${error.pluginKey}`,
      };
    }
    // 处理 RateLimitError - 使用业务错误码作为 status_code
    const err = error as any;
    if (err?.code === 'RATE_LIMIT_EXCEEDED' && err?.rateLimitCode) {
      return {
        status_code: err.rateLimitCode,
        error_msg: err.rateLimitMessage || err.message,
        is_rate_limit_error: true,
      };
    }
    return {
      status_code: ErrorCodes.EXECUTION_ERROR,
      error_msg: `Execution failed: ${error instanceof Error ? error.message : String(error)}`,
    };
  }

  @Post('debug/:capability_id/stream')
  async debugStream(
    @Param('capability_id') capabilityId: string,
    @Body() body: DebugRequestBody,
    @Res() res: Response,
  ): Promise<void> {
    const params = body.params ?? {};

    // 设置 SSE 响应头
    res.setHeader('Content-Type', 'text/event-stream');
    res.setHeader('Cache-Control', 'no-cache');
    res.setHeader('Connection', 'keep-alive');

    try {
      const config = this.getCapabilityConfig(capabilityId, body.capability);
      const action = await this.getActionName(config.pluginKey, config.formValue, body.action);
      const loggerContext = {
        capability_id: config.id,
        plugin_key: config.pluginKey,
        action,
      };

      // 打印入参
      this.logger.log(`Executing capability (stream)`, {
        ...loggerContext,
        input: stringifyForLog(params),
      });

      const capability = this.capabilityService.loadWithConfig(config);
      const eventStream = capability.callStreamWithEvents(action, params, { isDebug: true, caller: 'debug' });

      // 延迟发送策略：缓存上一个 chunk，收到下一个 data 或 done 时再发送
      // 这样可以在最后一个 chunk 上标记 finished: true
      let pendingChunk: unknown | null = null;

      for await (const event of eventStream) {
        switch (event.type) {
          case 'data': {
            // 先发送上一个缓存的 chunk（如果有）
            if (pendingChunk !== null) {
              const response: StreamContentResponse = {
                status_code: ErrorCodes.SUCCESS,
                data: {
                  type: 'content',
                  delta: pendingChunk,  // 直接透传 OutputSchema
                },
              };
              res.write(`data: ${JSON.stringify(response)}\n\n`);
            }
            // 缓存当前 chunk
            pendingChunk = event.data;
            break;
          }

          case 'done': {
            // 发送最后一个 chunk 并带上 finished: true
            if (pendingChunk !== null) {
              const response: StreamContentResponse = {
                status_code: ErrorCodes.SUCCESS,
                data: {
                  type: 'content',
                  delta: pendingChunk,  // 直接透传 OutputSchema
                  finished: true,
                },
              };
              res.write(`data: ${JSON.stringify(response)}\n\n`);
            }
            // 打印聚合结果（从 done 事件的 metadata 中获取）
            this.logger.log(`Capability (stream) executed successfully`, {
              ...loggerContext,
              duration_ms: event.metadata.duration,
              output: stringifyForLog(event.metadata.aggregated),
            });
            res.end();
            return;
          }

          case 'error': {
            // 错误事件：发送错误信息
            // RateLimitError 使用业务错误码
            const isRateLimitError = event.error.code === 'RATE_LIMIT_EXCEEDED' && !!event.error.rateLimitCode;
            const response: StreamErrorResponse = {
              status_code: ErrorCodes.SUCCESS,
              data: {
                type: 'error',
                error: {
                  code: isRateLimitError ? event.error.rateLimitCode! : event.error.code,
                  message: isRateLimitError ? event.error.rateLimitMessage! : event.error.message,
                  isRateLimitError: isRateLimitError || undefined,
                },
              },
            };
            res.write(`data: ${JSON.stringify(response)}\n\n`);
            res.end();
            return;
          }
        }
      }

      // 如果事件流正常结束但没有 done 事件，发送缓存的 chunk
      if (pendingChunk !== null) {
        const response: StreamContentResponse = {
          status_code: ErrorCodes.SUCCESS,
          data: {
            type: 'content',
            delta: pendingChunk,  // 直接透传 OutputSchema
            finished: true,
          },
        };
        res.write(`data: ${JSON.stringify(response)}\n\n`);
      }
      res.end();
    } catch (error) {
      // 流异常中断（在事件流开始前的错误）
      const err = error as any;
      const isRateLimitError = err?.code === 'RATE_LIMIT_EXCEEDED' && !!err?.rateLimitCode;
      const response: StreamErrorResponse = {
        status_code: ErrorCodes.SUCCESS,
        data: {
          type: 'error',
          error: {
            code: isRateLimitError ? err.rateLimitCode : (err?.code ?? 'EXECUTION_ERROR'),
            message: isRateLimitError ? err.rateLimitMessage : (error instanceof Error ? error.message : String(error)),
            isRateLimitError: isRateLimitError || undefined,
          },
        },
      };
      res.write(`data: ${JSON.stringify(response)}\n\n`);
      res.end();
    }
  }
}
